import { Quaternion, Vector3 } from 'three'

import { AnyID, Item, LinkedItem, SceneObjects, SnapSide, SnappedSides } from '@types'

import isLinkedItem from './assertions/isLinkedItem'
import { getMorphedItemWidth } from './projectItemOnSlabFaces'
import { OPPOSITE_SIDE, getObbCenter, getSideFacing } from './snappedSides'

/**
 * Detección única de grupos de items de la escena, basada SOLO en snappedSides.
 *
 * Cada item conoce quién ocupa sus 4 caras (left/right/front/back). Ese registro es la
 * única fuente de verdad: aquí no se mide geometría para decidir quién toca a quién, solo
 * para orientar (saber si un vecino cae al extremo izquierdo o derecho, y medir los giros).
 * Los items que llegan sin snappedSides —proyectos guardados antes de que existiera el
 * registro, o items cuyos ids se remapearon al importar— deben pasar antes por
 * `rebuildSnappedSides`, que los reconstruye desde la geometría una sola vez.
 *
 * El grafo distingue tres tipos de unión, y de ahí sale la forma del grupo:
 *
 * - `lateral`      ambos se tocan por left/right → van seguidos en la misma fila.
 * - `perpendicular` uno pone left/right y el otro front/back → la fila gira 90° (una L:
 *                   es el caso del rinconero, que sujeta al siguiente por su cara frontal).
 * - `depth`        ambos ponen front/back → van en paralelo, espalda contra espalda o
 *                   frente contra espalda: filas distintas de una misma isla.
 *
 * Una "fila" es una cadena de items pegados a lo ancho (puede girar esquinas). Un grupo es
 * el conjunto de filas conectadas entre sí. La forma sale de cuántas filas hay, cómo se
 * unen y cuántos giros da cada fila.
 */

/** Las 4 caras del plano XZ, siempre en este orden para que el resultado sea estable. */
export const SNAP_SIDES: readonly SnapSide[] = ['left', 'right', 'front', 'back']

/** Caras por las que la fila continúa a lo ancho. Front/back son profundidad. */
const isRunSide = (side: SnapSide) => side === 'left' || side === 'right'

/** Giro mínimo (grados) entre dos tramos consecutivos para contarlo como esquina. */
const CORNER_MIN_DEGREES = 30

/** Lo mínimo que necesita el detector de cualquier item de la escena. */
export type SnapGroupNode = Item | LinkedItem

export type SnapLinkKind = 'lateral' | 'perpendicular' | 'depth'

export type SnapLink = {
  from: AnyID
  to: AnyID
  /** Cara de `from` que ocupa `to` */
  fromSide: SnapSide
  /** Cara de `to` que ocupa `from` */
  toSide: SnapSide
  kind: SnapLinkKind
}

/**
 * - `single`  un solo item.
 * - `line`    una fila recta.
 * - `L`       una fila con un giro.
 * - `U`       una fila con dos giros del mismo sentido.
 * - `Z`       una fila con dos giros de sentido contrario.
 * - `zigzag`  una fila con tres o más giros.
 * - `ring`    una fila que se cierra sobre sí misma.
 * - `island`  dos o más filas unidas por sus caras front/back (isla, doble fila, península).
 * - `cross`   algún item con las 4 caras ocupadas.
 * - `T`       algún item con 3 caras ocupadas: una fila con un brazo colgando.
 * - `complex` cualquier otra topología (grafos ramificados que no encajan arriba).
 */
export type SnapGroupShape =
  'single' | 'line' | 'L' | 'U' | 'Z' | 'zigzag' | 'ring' | 'island' | 'cross' | 'T' | 'complex'

export type SnapRowSegment = {
  /** Ids del tramo recto, en el orden de la fila */
  items: AnyID[]
  /** Sentido del giro con el que arranca el tramo respecto del anterior; 0 el primero */
  turnFromPrevious: number
}

export type SnapRow = {
  /** Ids ordenados a lo largo de la fila, de izquierda a derecha vista desde los muebles */
  items: AnyID[]
  /** Tramos rectos en los que se parte la fila: una línea tiene 1, una L tiene 2 */
  segments: SnapRowSegment[]
  /** Sentido de cada giro de la fila: +1 y -1 son giros contrarios. Una L tiene uno */
  turns: number[]
  /** La fila se cierra sobre sí misma (rectángulo cerrado) */
  closed: boolean
}

export type SnapGroup = {
  /** Id estable del grupo: el menor id de sus miembros, no cambia entre recálculos */
  id: AnyID
  /** Todos los miembros, en orden de fila */
  items: AnyID[]
  rows: SnapRow[]
  shape: SnapGroupShape
  links: SnapLink[]
  /** Uniones front/front, front/back o back/back: lo que convierte varias filas en isla */
  depthLinks: SnapLink[]
  /** Giros que no caben en la fila porque el item ya tiene sus dos extremos ocupados */
  branchLinks: SnapLink[]
  /** Items con las 4 caras ocupadas dentro del grupo */
  crossItems: AnyID[]
  /** Items con exactamente 3 caras ocupadas dentro del grupo */
  junctionItems: AnyID[]
  /** Ocupación de caras ya filtrada a miembros del grupo (sin paredes ni tabiques) */
  neighbors: Map<AnyID, SnappedSides>
  /**
   * Los dos extremos a lo ancho de cada mueble y quién los ocupa. Es lo que hay que mirar
   * para saber si un mueble es principio o final de la fila —y por tanto si lleva lateral
   * o si ahí arranca un frontal—, NO `snappedSides.left/right`: un rinconero continúa la
   * fila por su cara frontal, así que su extremo derecho está ocupado aunque
   * `snappedSides.right` esté vacío.
   */
  runSlots: Map<AnyID, { left?: AnyID; right?: AnyID }>
}

type NodeGeometry = { center: Vector3; quaternion: Quaternion; xAxis: Vector3 }

type RunSlots = { left?: AnyID; right?: AnyID }

/**
 * Muebles de la escena que participan en el registro de caras: visibles y de catálogo.
 * Los frontales son piezas que se dibujan encima de un grupo ya formado, no muebles, y
 * meterlos partiría las filas. El mismo filtro lo usan getContItems y la hidratación, para
 * que agrupar y registrar caras vean exactamente el mismo conjunto.
 */
export function getSnapCandidates(items: Iterable<SceneObjects>): LinkedItem[] {
  const candidates: LinkedItem[] = []
  for (const item of items) {
    if (!isLinkedItem(item) || !item.visible) continue
    if ((item.catalogID?.toString().toLowerCase() ?? '').includes('frontal')) continue
    candidates.push(item)
  }
  return candidates
}

/**
 * Agrupa los items por sus snappedSides y clasifica la forma de cada grupo.
 * Solo cuentan como vecinos los ids presentes en `nodes`: las paredes y tabiques que un
 * item guarda en su cara trasera se descartan solos, igual que los items ya filtrados
 * por el llamador (invisibles, frontales…).
 */
export function getSnapGroups(nodes: Iterable<SnapGroupNode>): SnapGroup[] {
  const byId = new Map<AnyID, SnapGroupNode>()
  for (const node of nodes) {
    if (node?.id) byId.set(node.id as AnyID, node)
  }

  const geometry = new Map<AnyID, NodeGeometry>()
  for (const [id, node] of byId) geometry.set(id, getNodeGeometry(node))

  const neighbors = buildNeighbors(byId, geometry)
  const links = buildLinks(byId, neighbors, geometry)

  return buildGroups(byId, neighbors, links, geometry)
}

/** Centro real del OBB y eje X del item (su "a lo ancho" en coordenadas de mundo) */
function getNodeGeometry(node: SnapGroupNode): NodeGeometry {
  const position = new Vector3()
  const quaternion = new Quaternion()
  node.matrix.decompose(position, quaternion, new Vector3())
  const center = getObbCenter(
    position,
    quaternion,
    getMorphedItemWidth(node),
    node.height || 0,
    node.thickness || 0,
  )
  return { center, quaternion, xAxis: new Vector3(1, 0, 0).applyQuaternion(quaternion) }
}

/**
 * Ocupación de caras de cada item, filtrada a los miembros del conjunto y con los
 * recíprocos completados: collideItems registra las dos caras, pero un item movido
 * después puede haber perdido la suya. Sin el recíproco el grafo seguiría conectado,
 * pero la cara del vecino contaría como libre y el recuento de cruces/T saldría mal.
 */
function buildNeighbors(
  byId: Map<AnyID, SnapGroupNode>,
  geometry: Map<AnyID, NodeGeometry>,
): Map<AnyID, SnappedSides> {
  const neighbors = new Map<AnyID, SnappedSides>()
  for (const id of byId.keys()) neighbors.set(id, {})

  for (const [id, node] of byId) {
    const own = neighbors.get(id)!
    for (const side of SNAP_SIDES) {
      const otherId = node.snappedSides?.[side]
      if (!otherId || otherId === id || !byId.has(otherId)) continue
      own[side] = otherId
    }
  }

  for (const id of byId.keys()) {
    const own = neighbors.get(id)!
    for (const side of SNAP_SIDES) {
      const otherId = own[side]
      if (!otherId) continue
      const otherSides = neighbors.get(otherId)!
      if (SNAP_SIDES.some((otherSide) => otherSides[otherSide] === id)) continue
      const otherGeometry = geometry.get(otherId)!
      const facing = getSideFacing(
        otherGeometry.quaternion,
        otherGeometry.center,
        geometry.get(id)!.center,
      )
      if (!otherSides[facing]) otherSides[facing] = id
    }
  }

  return neighbors
}

/** Una entrada por pareja de items unidos, con la cara que pone cada uno */
function buildLinks(
  byId: Map<AnyID, SnapGroupNode>,
  neighbors: Map<AnyID, SnappedSides>,
  geometry: Map<AnyID, NodeGeometry>,
): SnapLink[] {
  const links: SnapLink[] = []
  const seen = new Set<string>()

  for (const id of byId.keys()) {
    const own = neighbors.get(id)!
    for (const side of SNAP_SIDES) {
      const otherId = own[side]
      if (!otherId) continue
      const key = id < otherId ? `${id}|${otherId}` : `${otherId}|${id}`
      if (seen.has(key)) continue
      seen.add(key)
      const otherSides = neighbors.get(otherId)!
      const toSide =
        SNAP_SIDES.find((otherSide) => otherSides[otherSide] === id) ?? OPPOSITE_SIDE[side]
      const turned = isTurnBetween(geometry.get(id), geometry.get(otherId))
      links.push({ from: id, to: otherId, fromSide: side, toSide, kind: getLinkKind(side, toSide, turned) })
    }
  }

  return links
}

/** Coseno de 60°: por debajo, dos ejes X ya no cuentan como paralelos */
const TURN_AXIS_DOT_THRESHOLD = 0.5

/**
 * True si `a` y `b` están girados entre sí (sus anchos ya no corren en paralelo). Sirve
 * para distinguir, cuando los dos ponen front/back, una esquina en L de un verdadero
 * fondo contra fondo (isla): el rinconero registra su giro en L como `front` aunque el
 * vecino solo apoye ahí su trasera genuina (la que lo pega a la pared perpendicular), así
 * que las dos caras salen `front`/`back` en ambos casos y solo la orientación real las
 * distingue.
 */
function isTurnBetween(a: NodeGeometry | undefined, b: NodeGeometry | undefined): boolean {
  if (!a || !b) return false
  return Math.abs(a.xAxis.dot(b.xAxis)) < TURN_AXIS_DOT_THRESHOLD
}

function getLinkKind(fromSide: SnapSide, toSide: SnapSide, turned: boolean): SnapLinkKind {
  if (isRunSide(fromSide) && isRunSide(toSide)) return 'lateral'
  if (isRunSide(fromSide) !== isRunSide(toSide)) return 'perpendicular'
  return turned ? 'perpendicular' : 'depth'
}

function buildGroups(
  byId: Map<AnyID, SnapGroupNode>,
  neighbors: Map<AnyID, SnappedSides>,
  links: SnapLink[],
  geometry: Map<AnyID, NodeGeometry>,
): SnapGroup[] {
  const linksByItem = new Map<AnyID, SnapLink[]>()
  for (const link of links) {
    if (!linksByItem.has(link.from)) linksByItem.set(link.from, [])
    if (!linksByItem.has(link.to)) linksByItem.set(link.to, [])
    linksByItem.get(link.from)!.push(link)
    linksByItem.get(link.to)!.push(link)
  }

  const groups: SnapGroup[] = []
  const visited = new Set<AnyID>()

  for (const id of byId.keys()) {
    if (visited.has(id)) continue

    // Componente conexa: todo lo que se alcanza saltando de cara en cara
    const members: AnyID[] = []
    const groupLinks: SnapLink[] = []
    const seenLinks = new Set<SnapLink>()
    const queue: AnyID[] = [id]
    visited.add(id)
    while (queue.length) {
      const current = queue.shift()!
      members.push(current)
      for (const link of linksByItem.get(current) ?? []) {
        if (!seenLinks.has(link)) {
          seenLinks.add(link)
          groupLinks.push(link)
        }
        const other = link.from === current ? link.to : link.from
        if (visited.has(other)) continue
        visited.add(other)
        queue.push(other)
      }
    }

    groups.push(buildGroup(members, groupLinks, neighbors, geometry))
  }

  return groups
}

function buildGroup(
  members: AnyID[],
  links: SnapLink[],
  neighbors: Map<AnyID, SnappedSides>,
  geometry: Map<AnyID, NodeGeometry>,
): SnapGroup {
  const { slots, branchLinks } = assignRunSlots(members, links, geometry)
  const rows = buildRows(members, slots, geometry)
  const depthLinks = links.filter((link) => link.kind === 'depth')

  const groupNeighbors = new Map<AnyID, SnappedSides>()
  const crossItems: AnyID[] = []
  const junctionItems: AnyID[] = []
  for (const member of members) {
    const sides = neighbors.get(member) ?? {}
    groupNeighbors.set(member, sides)
    const occupied = SNAP_SIDES.filter((side) => sides[side]).length
    if (occupied >= 4) crossItems.push(member)
    else if (occupied === 3) junctionItems.push(member)
  }

  return {
    id: members.reduce((lowest, member) => (member < lowest ? member : lowest), members[0]),
    items: rows.flatMap((row) => row.items),
    rows,
    shape: classifyShape({ members, rows, depthLinks, branchLinks, crossItems, junctionItems }),
    links,
    depthLinks,
    branchLinks,
    crossItems,
    junctionItems,
    neighbors: groupNeighbors,
    runSlots: slots,
  }
}

/**
 * Reparte las uniones de fila en los dos extremos (izquierdo y derecho) de cada item, que
 * es todo lo que un mueble puede encadenar a lo ancho. Los laterales puros van primero:
 * ocupan su extremo natural antes de que un giro se lo quite. Un giro entra por el extremo
 * al que cae el vecino. Lo que no cabe queda como `branchLinks`: es un brazo que sale de un
 * item ya encadenado por los dos lados, o sea una T.
 */
function assignRunSlots(
  members: AnyID[],
  links: SnapLink[],
  geometry: Map<AnyID, NodeGeometry>,
): { slots: Map<AnyID, RunSlots>; branchLinks: SnapLink[] } {
  const slots = new Map<AnyID, RunSlots>(members.map((member) => [member, {}]))
  const branchLinks: SnapLink[] = []

  const runLinks = [
    ...links.filter((link) => link.kind === 'lateral'),
    ...links.filter((link) => link.kind === 'perpendicular'),
  ]

  for (const link of runLinks) {
    const fromSlot = pickRunSlot(link.from, link.fromSide, link.to, slots, geometry)
    const toSlot = pickRunSlot(link.to, link.toSide, link.from, slots, geometry)
    if (!fromSlot || !toSlot) {
      branchLinks.push(link)
      continue
    }
    slots.get(link.from)![fromSlot] = link.to
    slots.get(link.to)![toSlot] = link.from
  }

  return { slots, branchLinks }
}

function pickRunSlot(
  id: AnyID,
  side: SnapSide,
  otherId: AnyID,
  slots: Map<AnyID, RunSlots>,
  geometry: Map<AnyID, NodeGeometry>,
): 'left' | 'right' | undefined {
  const own = slots.get(id)
  if (!own) return undefined
  if (isRunSide(side)) return own[side] ? undefined : side

  // Giro: el vecino está delante o detrás, así que el extremo lo decide hacia qué lado
  // del ancho del item cae.
  const self = geometry.get(id)!
  const towards = new Vector3().subVectors(geometry.get(otherId)!.center, self.center)
  const preferred = self.xAxis.dot(towards) < 0 ? 'left' : 'right'
  if (!own[preferred]) return preferred
  const fallback = preferred === 'left' ? 'right' : 'left'
  return own[fallback] ? undefined : fallback
}

/** Cada cadena de extremos encadenados es una fila, ya ordenada a lo largo */
function buildRows(
  members: AnyID[],
  slots: Map<AnyID, RunSlots>,
  geometry: Map<AnyID, NodeGeometry>,
): SnapRow[] {
  const rows: SnapRow[] = []
  const visited = new Set<AnyID>()

  for (const member of members) {
    if (visited.has(member)) continue

    const rowMembers = new Set<AnyID>()
    const queue: AnyID[] = [member]
    visited.add(member)
    while (queue.length) {
      const current = queue.shift()!
      rowMembers.add(current)
      const currentSlots = slots.get(current)!
      for (const neighbor of [currentSlots.left, currentSlots.right]) {
        if (!neighbor || visited.has(neighbor)) continue
        visited.add(neighbor)
        queue.push(neighbor)
      }
    }

    rows.push(orderRow(rowMembers, slots, geometry))
  }

  return rows
}

function orderRow(
  rowMembers: Set<AnyID>,
  slots: Map<AnyID, RunSlots>,
  geometry: Map<AnyID, NodeGeometry>,
): SnapRow {
  const sorted = [...rowMembers].sort()
  const endpoints = sorted.filter((id) => {
    const own = slots.get(id)!
    return !own.left || !own.right
  })
  const closed = endpoints.length === 0 && rowMembers.size > 1
  const start = endpoints[0] ?? sorted[0]

  const items: AnyID[] = []
  const seen = new Set<AnyID>()
  let current: AnyID | undefined = start
  let previous: AnyID | undefined
  while (current && !seen.has(current)) {
    seen.add(current)
    items.push(current)
    const own: RunSlots = slots.get(current)!
    // Se sale por el extremo por el que no se ha entrado
    let next: AnyID | undefined
    if (own.left && own.left !== previous) next = own.left
    else if (own.right && own.right !== previous) next = own.right
    previous = current
    current = next
  }

  orientRow(items, geometry)

  const turns = getRowTurns(items, geometry)
  return {
    items,
    turns: turns.map((turn) => turn.side),
    segments: splitIntoSegments(items, turns),
    closed,
  }
}

/** Parte la fila por sus esquinas: cada tramo recto es una alineación de muebles */
function splitIntoSegments(items: AnyID[], turns: RowTurn[]): SnapRowSegment[] {
  const segments: SnapRowSegment[] = []
  let current: AnyID[] = []
  let turnIntoCurrent = 0
  for (let index = 0; index < items.length; index += 1) {
    const turn = turns.find((candidate) => candidate.index === index)
    if (turn && current.length) {
      segments.push({ items: current, turnFromPrevious: turnIntoCurrent })
      current = []
      turnIntoCurrent = turn.side
    }
    current.push(items[index])
  }
  if (current.length) segments.push({ items: current, turnFromPrevious: turnIntoCurrent })
  return segments
}

/**
 * Deja la fila en el sentido izquierda→derecha visto desde los propios muebles: cada item
 * vota con su eje X hacia dónde apunta el siguiente, y gana la mayoría. Votan todos y no
 * solo el primero para que las L (donde media fila apunta en otra dirección) no se
 * inviertan por el mueble de la esquina.
 */
function orientRow(items: AnyID[], geometry: Map<AnyID, NodeGeometry>) {
  if (items.length < 2) return
  let vote = 0
  for (let index = 0; index < items.length - 1; index += 1) {
    const self = geometry.get(items[index])!
    const towards = new Vector3().subVectors(geometry.get(items[index + 1])!.center, self.center)
    vote += Math.sign(self.xAxis.dot(towards))
  }
  if (vote < 0) items.reverse()
}

/**
 * Dirección en la que corre cada mueble a lo largo de la fila: su eje X (por donde va su
 * ancho), con el sentido que sigue a la fila.
 *
 * No vale medir el giro con la línea que une los centros: un rinconero es mucho más
 * profundo que sus vecinos y su centro queda muy descolocado respecto de la alineación,
 * así que una L puede salir casi recta o una recta salir torcida. Lo que gira de verdad en
 * una esquina es la orientación de los muebles.
 *
 * El eje X apunta a un lado u otro según cómo esté colocado cada mueble, así que se elige
 * el sentido que no retrocede sobre el tramo anterior. En un giro de 90° los dos sentidos
 * empatan (el producto escalar es 0) y desempata hacia dónde queda el vecino.
 */
function getRowDirections(items: AnyID[], geometry: Map<AnyID, NodeGeometry>): Vector3[] {
  // Un mueble solo no tiene fila que seguir: su propio eje ya es la dirección.
  if (items.length < 2) return items.map((id) => geometry.get(id)!.xAxis.clone())

  const stepBetween = (from: AnyID, to: AnyID) =>
    new Vector3().subVectors(geometry.get(to)!.center, geometry.get(from)!.center)

  const directions: Vector3[] = []
  for (let index = 0; index < items.length; index += 1) {
    const axis = geometry.get(items[index])!.xAxis.clone()
    let sign = index > 0 ? signOrZero(directions[index - 1].dot(axis)) : 0
    if (sign === 0) {
      const towards =
        index < items.length - 1
          ? stepBetween(items[index], items[index + 1])
          : stepBetween(items[index - 1], items[index])
      sign = signOrZero(axis.dot(towards)) || 1
    }
    directions.push(axis.multiplyScalar(sign))
  }
  return directions
}

/** Tolerancia del empate al orientar un mueble: por debajo se considera perpendicular */
const AXIS_TIE_TOLERANCE = 1e-6
const signOrZero = (value: number) => (Math.abs(value) <= AXIS_TIE_TOLERANCE ? 0 : Math.sign(value))

type RowTurn = { /** Índice del primer mueble del tramo nuevo */ index: number; side: number }

/** Esquinas de la fila: una L tiene una, una U dos del mismo signo */
function getRowTurns(items: AnyID[], geometry: Map<AnyID, NodeGeometry>): RowTurn[] {
  const directions = getRowDirections(items, geometry)
  const turns: RowTurn[] = []
  for (let index = 0; index < directions.length - 1; index += 1) {
    const inbound = directions[index]
    const outbound = directions[index + 1]
    const cross = inbound.x * outbound.z - inbound.z * outbound.x
    const degrees = Math.abs((Math.atan2(cross, inbound.dot(outbound)) * 180) / Math.PI)
    if (degrees >= CORNER_MIN_DEGREES) turns.push({ index: index + 1, side: Math.sign(cross) || 1 })
  }
  return turns
}

/**
 * Forma del grupo. Se comprueba en este orden porque las reglas se solapan: el mueble
 * central de una isla 2x3 tiene 3 caras ocupadas y parecería una T, así que la isla manda;
 * y una cruz tiene 4 caras ocupadas, que también son 3 más una, así que va antes que la T.
 */
function classifyShape({
  members,
  rows,
  depthLinks,
  branchLinks,
  crossItems,
  junctionItems,
}: {
  members: AnyID[]
  rows: SnapRow[]
  depthLinks: SnapLink[]
  branchLinks: SnapLink[]
  crossItems: AnyID[]
  junctionItems: AnyID[]
}): SnapGroupShape {
  if (members.length === 1) return 'single'

  // Dos filas con dos o más uniones de fondo entre ellas corren en paralelo: isla.
  const rowOfItem = new Map<AnyID, number>()
  rows.forEach((row, index) => row.items.forEach((item) => rowOfItem.set(item, index)))
  const depthLinksByRowPair = new Map<string, number>()
  for (const link of depthLinks) {
    const fromRow = rowOfItem.get(link.from)
    const toRow = rowOfItem.get(link.to)
    if (fromRow === undefined || toRow === undefined) continue
    const key = fromRow < toRow ? `${fromRow}|${toRow}` : `${toRow}|${fromRow}`
    depthLinksByRowPair.set(key, (depthLinksByRowPair.get(key) ?? 0) + 1)
  }
  if ([...depthLinksByRowPair.values()].some((count) => count >= 2)) return 'island'

  if (crossItems.length) return 'cross'
  if (junctionItems.length || branchLinks.length) return 'T'

  if (rows.length === 1) {
    const [row] = rows
    if (row.closed) return 'ring'
    const turns = row.turns
    if (turns.length === 0) return 'line'
    if (turns.length === 1) return 'L'
    if (turns.length === 2) return turns[0] === turns[1] ? 'U' : 'Z'
    return 'zigzag'
  }

  if (depthLinks.length) return 'island'
  return 'complex'
}
